import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Initialize debug log storage BEFORE any components load
window.__debugLogs = window.__debugLogs || [];
const MAX_LOGS = 100;

if (!window.__debugConsoleIntercepted) {
  window.__debugConsoleIntercepted = true;
  
  const addLog = (type, args) => {
    const message = args.map(arg => 
      typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
    ).join(' ');
    
    window.__debugLogs.unshift({
      type,
      message,
      timestamp: new Date().toISOString()
    });
    
    if (window.__debugLogs.length > MAX_LOGS) {
      window.__debugLogs.pop();
    }
  };

  const originalConsoleLog = console.log;
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;

  console.log = (...args) => {
    addLog('log', args);
    originalConsoleLog.apply(console, args);
  };

  console.error = (...args) => {
    addLog('error', args);
    originalConsoleError.apply(console, args);
  };

  console.warn = (...args) => {
    addLog('warn', args);
    originalConsoleWarn.apply(console, args);
  };
}

// Pages
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import BudgetGoals from "@/pages/BudgetGoals";
import Categories from "@/pages/Categories";
import Import from "@/pages/Import";
import QuickCheck from "@/pages/QuickCheck";
import Retirement from "@/pages/Retirement";
import OtherSavings from "@/pages/OtherSavings";
import LongTermPlanning from "@/pages/LongTermPlanning";
import Reconcile from "@/pages/Reconcile";
import Layout from "@/components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// API instance with auth
export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth Provider Component
function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      api.get("/auth/me")
        .then((res) => {
          setUser(res.data);
          localStorage.setItem("user", JSON.stringify(res.data));
        })
        .catch(() => {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (username, password) => {
    console.log('[AUTH] Login attempt started', { username, timestamp: new Date().toISOString() });
    try {
      const res = await api.post("/auth/login", { username, password });
      console.log('[AUTH] Login successful', { username, userId: res.data.user?.id });
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      setUser(res.data.user);
      return res.data;
    } catch (error) {
      console.error('[AUTH] Login failed', {
        username,
        status: error.response?.status,
        statusText: error.response?.statusText,
        detail: error.response?.data?.detail,
        message: error.message,
        code: error.code
      });
      throw error;
    }
  };

  const register = async (username, password, displayName, passwordHint = null, inviteCode = null) => {
    const res = await api.post("/auth/register", {
      username,
      password,
      display_name: displayName,
      password_hint: passwordHint,
      invite_code: inviteCode,
    });
    localStorage.setItem("token", res.data.token);
    localStorage.setItem("user", JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="goals" element={<BudgetGoals />} />
            <Route path="categories" element={<Categories />} />
            <Route path="import" element={<Import />} />
            <Route path="reconcile" element={<Reconcile />} />
            <Route path="quick-check" element={<QuickCheck />} />
            <Route path="retirement" element={<Retirement />} />
            <Route path="other-savings" element={<OtherSavings />} />
            <Route path="long-term" element={<LongTermPlanning />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
